import {
  CodexEntrySchema,
  CoreEntityType,
  NovelActSchema,
  NovelActTree,
  NovelChapterSchema,
  NovelSceneSchema
} from "#/index";
import { convertHtmlToFormattedText, convertJSONContentToFormattedText } from "@/utils";
import { getCodexEntryFields } from ".";
import { ExecutionContext } from "../types";


/**
 * 将codex条目转换为XML格式
 * @param codexEntry 代码库条目
 * @returns XML格式的代码库条目
 */
export const codexEntriesToXml = (context: ExecutionContext, codexEntries: CodexEntrySchema[]) => {

  return codexEntries.map(codexEntry => {
    let lineAttr = `name="${codexEntry.attributes.name}"`
    let description: string | undefined;
    const fields = getCodexEntryFields(context, codexEntry);
    if (codexEntry.attributes.aliases) {
      lineAttr += ` aliases="${codexEntry.attributes.aliases}"`
    }
    fields.forEach(field => {
      if (field.type !== 'text') {
        lineAttr += ` line="${field.value}"`;
      }
      if (field.type === 'text') {
        if (!description) {
          description = `<description>${field.value}</description>`
        }
        description += `\n<${field.name}>${field.value}</${field.name}>`
      }
    });

    if (!description) {
      description = codexEntry.attributes.description
    }
    return `<${codexEntry.attributes.type} ${lineAttr}>${description}</${codexEntry.attributes.type}>`;
  });
}


/**
 * 生成场景的XML
 * @param scene 场景数据
 * @returns XML格式的场景数据
 */
export const parseSceneToXML = (scene: NovelActTree, context: ExecutionContext, type?: 'content' | 'summary'): string => {
  const sceneData = scene.data as NovelSceneSchema;
  const title = sceneData.attributes.title || `Scene ${scene.realIndex}`;
  const number = scene.realIndex;
  let content: string = '';
  if (type === 'content' && sceneData.attributes.content) {
    content = convertJSONContentToFormattedText(sceneData.attributes.content);
  } else if (type === 'summary' && sceneData.attributes.summary) {
    content = convertHtmlToFormattedText(sceneData.attributes.summary);
  }

  const pointOfView = sceneData.attributes.pov;
  const povCharacterId = sceneData.attributes.pov?.characterId;
  const povCharacter = povCharacterId
    ? context.globalContext.generateStore.getState()
      .codexEntries.find(character => character.id === povCharacterId)?.attributes.name
    : '';

  return `<scene title="${title}" number="${number}" ${pointOfView ? `pointOfView="${pointOfView}" ` : ''} ${povCharacter ? `povCharacter="${povCharacter}" ` : ''}>${content}</scene>`;
};

/**
 * 生成章节的XML
 * @param chapter 章节数据
 * @returns XML格式的章节数据
 */
export const parseChapterToXML = (chapter: NovelActTree, context: ExecutionContext, type?: 'content' | 'summary'): string => {
  const chapterData = chapter.data as NovelChapterSchema;
  const title = chapterData.attributes.title ? ` title="${chapterData.attributes.title}"` : '';
  const number = chapter.realIndex;

  let scenesXML = '';
  if (chapter.children) {
    scenesXML = chapter.children
      .filter(child => child.data.type === CoreEntityType.NovelScenes)
      .map(scene => parseSceneToXML(scene, context, type))
      .join('\n    ');
  }

  return `<chapter${title} number="${number}">
    ${scenesXML}
  </chapter>`;
};

/**
 * 生成卷的XML
 * @param act 卷数据
 * @returns XML格式的卷数据
 */
export const parseActToXML = (act: NovelActTree, context: ExecutionContext, type?: 'content' | 'summary'): string => {
  const actData = act.data as NovelActSchema;
  const title = actData.attributes.title ? ` title="${actData.attributes.title}"` : '';
  const number = act.realIndex;

  let chaptersXML = '';
  if (act.children) {
    chaptersXML = act.children
      .filter(child => child.data.type === CoreEntityType.NovelChapters)
      .map(chapter => parseChapterToXML(chapter, context, type))
      .join('\n    ');
  }

  return `<act${title} number="${number}">
    ${chaptersXML}
  </act>`;
};

export const parseOutlineToXML = (outline: NovelActTree[],
  context: ExecutionContext,
  rootName?: string,
  type?: 'content' | 'summary'): string => {
  const tag = rootName || 'novel';
  const actsXML = outline.map(act => parseActToXML(act, context, type)).join('\n  ');
  if (actsXML) {
    return `<${tag}>\n${actsXML}\n</${tag}>`;
  }
  return `<${tag}/>`;
}

/**
 * 解析小说数据为XML格式的完整大纲
 * @param data 小说数据
 * @returns XML格式的完整大纲数据
 */
export const parseFullSummaryToXML = (data: NovelActTree[], context: ExecutionContext): string => {
  const actsXML = data.map(act => parseActToXML(act, context, 'summary')).join('\n  ');
  if (actsXML) {
    return `<outline>\n${actsXML}\n</outline>`;
  }
  return '<outline/>';
}

/**
 * 解析小说数据为XML格式的完整内容
 * @param data 小说数据
 * @returns XML格式的完整大纲数据
 */
export const parseFullContentToXML = (data: NovelActTree[], context: ExecutionContext): string => {
  const actsXML = data.map(act => parseActToXML(act, context, 'content')).join('\n  ');
  if (actsXML) {
    return `<novel>\n${actsXML}\n</novel>`;
  }
  return '<novel/>';
}